import * as THREE from 'three';
import type { Sprite } from '../ui/data/spriteTypes';
import { FISH_PER_SPRITE, dayHurry, dayShare, hash3, pointOnRoute, reefPath, routeOf, spritesByIsland, type Route, type Swim } from './fishSwim';
import { FishBatch, fishAtlas, nearFade, type FishDraw } from './fishBatch';
import type { Caster } from './habitats/shadows';
import { COMMUTE_BL_PER_S, FORAGE_BL_PER_S, FORAGE_ORIENT_BL, NIGHT_BL_PER_S, ORIENT_BL, STEP_S, divesDown, feeding, noiseStream, patrol, stepSchool, type Swimmer } from './schooling';
import type { FishSpeciesId } from '../sim/fish';
import { LOD_HOLD, RECONCILE, heldInLevel } from './persistence';


/**
 * A reef fish's breadth seen from above, a share of its length (M246, its shadow's width): the reef's fish deep and
 * narrow bodied, their bodies a fifth as broad as they are long or less (JUDGED, a drawing's; the shadow is a soft blob).
 */
const FISH_BREADTH = 0.2;
/**
 * The aquarium's fish (M83; in schools since M88, the user's word: "make
 * the fish sprites appear in schools of the same type. you can increase
 * the amount of sprites, as long as they are in schools together"): each
 * species' initial phase in schools of its own kind on each island's reef,
 * each school's middle making its way along its commute between its night
 * shelter and its daytime feeding ground over the whole day, brightening
 * and rising as it comes out (M91, `fishSwim.ts`), its fish about the
 * middle as a school's are, milling slowly about its shelter at night,
 * never all still (the user's
 * word: "the fish should be moving continuously, not stop and go like it is
 * now"; `schooling.ts`), dropping to the reef
 * together to feed; the species' terminal males round their schools, each
 * patrolling its own ground. Sized in the scene, so they grow with the zoom
 * (the user's word), turned to face the way they last moved on screen,
 * dimmed while they shelter, and drawn together in one batch
 * (`fishBatch.ts`).
 */

/** The reef fish the game counts and does not simulate, drawn on the Reef from NOAA's divers' count (M231): the yellow tang. */
export type CountedReefFishId = 'yellowTang' | 'moray';

/** A species as the aquarium draws it: how it swims, and its sprites, the user's art (facing left). */
export interface FishKind {
  /** The species, as the sanctuary's fish name it; a counted one's as the registry does (`src/sim/species.ts`). */
  species: FishSpeciesId | CountedReefFishId;
  swim: Swim;
  initial: Sprite;
  /** Absent while the user's picture is not given: then none of the terminal males are drawn. */
  terminal?: Sprite;
  /** Fish a sprite of this kind stands for, where not the view's own (M97: the roi's, so its many are seen as a reef's, not a carpet). */
  perSprite?: number;
}

/** A species' fish on a day: the initial phase's and the terminal males', their mean total lengths, mm, and all its fish on each island, for the islands' shares. */
export interface KindCounts {
  initial: number;
  terminal: number;
  lengthMm: { initial: number; terminal: number };
  byIsland: readonly number[];
  /** The terminal males' own shares by island, where they are not the initial phase's (M118, Maui Nui Reef's). */
  terminalByIsland?: readonly number[];
}

/**
 * Scene units a fish is long for each mm of its group's mean total length on the map (M88, JUDGED: 243 mm, the
 * bullethead's initial phase on Maui Nui's first day when it was set, 0.18 units long; since M84's reef 191 mm,
 * 0.142; its schools a few km across
 * round the map's narrow reefs; the exhibit's larger, `EXHIBIT_SPRITES`). Each species and phase to scale
 * with the others, as on the card (M75), whatever the scale its picture was drawn at.
 */
export const UNITS_PER_MM = 0.18 / 243;
/** How many body lengths above the reef a school cruises (JUDGED: Colin's group "about 1 m above the substrate" is four lengths; drawn lower, so it keeps over its reef on the box's stretched slopes), and how low it drops to feed. */
const CRUISE_BL = 1.5;
const FEED_BL = 0.3;
/** How far below its school's point on the reef a fish swims at most, body lengths, over a falling floor (JUDGED: a school over a deep slope keeps to its reef's depth). */
const DROP_BL = 2;
/** How low it lies sheltering at night, body lengths, and how dim (JUDGED). */
const REST_BL = 0.2;
const SHELTERED_OPACITY = 0.45;
/**
 * The fastest a fish is drawn moving about its school's point, its lengths a second (JUDGED: over twice the
 * fastest it swims in its school, so it keeps its place there; the shore can draw it in faster, `lay`).
 */
const MOST_BL_PER_S = 3;
/**
 * The steps out from a school's point in which a fish's way is searched for the nearest shore, shares of the way
 * (so they move with the place, and the same shore is found as it moves; a strip of land narrower than a step can
 * fall between them, and a fish swims across it, `lay`), and the halvings that then find it (to a millionth of a
 * step: at 16 steps and 8 halvings, a male's place at the shore stepped back and forth and turned him about, M88's
 * review).
 */
const SHORE_STEPS = 32;
const SHORE_HALVINGS = 20;

/**
 * The share of the way out to a point (`ex`, `ez` off the school's point) before the first land along it, `wet` telling
 * water from land: searched in `SHORE_STEPS`, then found by `SHORE_HALVINGS` halvings; null where none. Its own function
 * since M154, so a test holds the search itself, not the chance of a school near a thin strip (M128's fish no longer
 * brought one there, and `fishSwim.test.ts` no longer caught the search made coarse).
 */
export function firstLand(ex: number, ez: number, wet: (x: number, z: number) => boolean): number | null {
  let land = 0;
  for (let i = 1; i <= SHORE_STEPS && land === 0; i++) if (!wet((ex * i) / SHORE_STEPS, (ez * i) / SHORE_STEPS)) land = i / SHORE_STEPS;
  if (land === 0) return null;
  let sea = land - 1 / SHORE_STEPS;
  for (let i = 0; i < SHORE_HALVINGS; i++) {
    const k = (sea + land) / 2;
    if (wet(ex * k, ez * k)) sea = k;
    else land = k;
  }
  return sea;
}
/** How far off the shoreline a fish's place drawn in to it is kept, its lengths (JUDGED: off it, as drawn in 32-bit floats; a millionth of the way from it, 53 fish were drawn on land). */
const SHORE_GAP_BL = 0.05;

/** A route as drawn: its legs the distances between its columns' places on the reef, scene units. */
function drawnRoute(route: Route, place: (column: number, out: THREE.Vector3) => THREE.Vector3): Route {
  const legs = route.path.slice(1).map((column, i) => place(route.path[i] as number, new THREE.Vector3()).distanceTo(place(column, new THREE.Vector3())));
  return { path: route.path, legs, total: legs.reduce((a, b) => a + b, 0) };
}
/**
 * The most swimming a frame moves the schools on, seconds (JUDGED: ten steps, 1.9 to 2.1 ms a frame for Maui Nui's 332
 * fish on M84's reef, against 0.53 to 0.67 ms for a frame's one step at the game's own speed through a whole day,
 * MEASURED in node over nine runs of 1,000 and 2,400 frames on the map; at 60 frames a second the schools' own motion
 * keeps the clock's pace to about 30 times the game's speed and runs slower above, as the clock's own days a frame are
 * capped, `main.ts`).
 */
const MAX_SWIM_S = 0.5;

/**
 * How the schools roam by day, where they do (`FishSchools`' `roam`); how far inside the box the fish are kept in
 * (`keepIn`) their school's middle is kept, scene units; and how far round each fish the floor it keeps over is read,
 * scene units (`RISE_BL_PER_S`'s), on a grid of `envelopeCell`.
 */
export interface Roam {
  points: number;
  blPerS: number;
  lean: number;
  rangeM: (kind: FishKind) => number | null;
  insetM: number;
  envelopeM: number;
  envelopeCell: number;
}

/** A loop on the level, scene units: its corners, from its first round to it again, its legs and its length. */
interface Loop {
  points: [number, number][];
  legs: number[];
  total: number;
}

/** Where along a loop a distance lies, and the loop's way there (a unit vector). */
function onLoop(loop: Loop, at: number): { x: number; z: number; wx: number; wz: number } {
  let left = ((at % loop.total) + loop.total) % loop.total;
  for (let i = 0; i < loop.legs.length; i++) {
    const leg = loop.legs[i] as number;
    const [a, b] = [loop.points[i] as [number, number], loop.points[(i + 1) % loop.points.length] as [number, number]];
    if (left <= leg || i === loop.legs.length - 1) {
      const t = leg > 0 ? Math.min(1, left / leg) : 0;
      const [dx, dz] = [b[0] - a[0], b[1] - a[1]];
      const d = Math.hypot(dx, dz) || 1;
      return { x: a[0] + dx * t, z: a[1] + dz * t, wx: dx / d, wz: dz / d };
    }
    left -= leg;
  }
  const [x, z] = loop.points[0] as [number, number];
  return { x, z, wx: 1, wz: 0 };
}

/**
 * How far a way on the level runs across the screen from `camera` at a place: the sine of its angle off the line of
 * sight, seen from above in the camera's frame, positive to the right; 0 for no way (M159; `facesRight`'s sign).
 */
export function acrossOf(camera: THREE.Camera, at: THREE.Vector3, way: THREE.Vector3, p: THREE.Vector3, d: THREE.Vector3): number {
  const w = Math.hypot(way.x, way.z);
  if (w <= 1e-9) return 0;
  p.copy(at).applyMatrix4(camera.matrixWorldInverse);
  d.set(way.x / w, 0, way.z / w).transformDirection(camera.matrixWorldInverse);
  const r = Math.hypot(p.x, p.z);
  return r > 1e-9 ? (d.x * -p.z + p.x * d.z) / r : 0;
}

/**
 * A fish of a school (M266): its place about the school's point and its heading (`Swimmer`), its own number among the
 * scene's fish, kept as it moves from school to school, and whether it is leaving the drawing, fading.
 */
interface Member extends Swimmer {
  id: number;
  leaving: boolean;
  /** Its level of detail's draw (`Drawn.key`), given as it first came: its first school's and its place there, as before M266. */
  key: number;
}

/** A fish's name (M266): its own number, whatever school it swims in. */
const memberId = (m: Member): string => `f${m.id}`;

/** A school: its kind and island, its commute, its initial-phase fish in body lengths about its middle, and its terminal males. */
interface School {
  /** Its draw's seed: its kind's, its island's and its number's. */
  seed: number;
  kind: number;
  island: number;
  route: Route;
  /** Its route as drawn, its legs in scene units, so its middle's pace along it is its pace on screen. */
  drawn: Route;
  fish: Member[];
  /** Its terminal males, at their posts by number (`patrol`); the last `malesLeaving` of them leaving, fading (M266). */
  males: number;
  malesLeaving: number;
  rng: () => number;
  /** Its feeding's and its males' beats' phase, 0 to 1. */
  phase: number;
  /** Seconds of swimming it has had, for its feeding's beat and its males' patrols. */
  seconds: number;
  /** Its share of the way out when last drawn. */
  share: number;
  /** How far it has risen from its shelter and brightened, 0 to 1 (as far as it has come out, `dayShare`), as last drawn. */
  awake: number;
  /** Whether it has yet to be drawn: a kind with its own day starts where its hour has it (`DayProfile`). */
  fresh: boolean;
  /** How much it was travelling when last drawn, 0 to 1, and which way (positive out). */
  travelling: number;
  /** Whether it is swimming to where a jump of the hour has put it (`JUMP_HOURS`), not yet there. */
  chasing: boolean;
  /** Its day's roaming loop (`Roam`), none where it does not roam, and how far round it it has gone, scene units. */
  loop: Loop | null;
  roamed: number;
  /** Its middle when last drawn, for its way. */
  lastMiddle: THREE.Vector3 | null;
  /** Its daylight's share for feeding when last drawn, 0 to 1 (M265). */
  daylight: number;
}

/**
 * A change of the hour between two frames too large for any clock to make smoothly, hours (JUDGED: the observation
 * clock runs at most 1.2 hours a second and a frame counts for at most 250 ms, 0.3 hours; Watch One Day's start
 * and its replay put the hour at midnight at once, M95's review); and the least time a school then swims to where
 * the new hour has it, seconds for a whole change (JUDGED: a commute's swim, never a snap; the brief's section 6.4).
 */
const JUMP_HOURS = 2;
const CHASE_S = 4;

/**
 * How much of the screen the fish may cover before fewer of them are drawn (M105, the redesign v2's section 4: "about
 * 15 to 30 recognizable fish" in a reef view on a phone): `most`, a share of the screen (JUDGED, set by the Reef's shot
 * on a phone to hold about that many, MEASURED in `tests/fishLod.test.ts`); `told`, at most how many fish at least
 * `toldPx` long are in view (JUDGED: the brief's 30 on a screen of any size; a fish that short is a speck, not told
 * apart, so the whole tank's small fish all stay); `easeS`, the seconds over which the level
 * goes about two thirds of the way to a new view's, whatever the frame rate (JUDGED: no flicker as the camera moves,
 * settled within a second of a move's end). Each fish fades in or out in time as the level passes its key
 * (`LOD_HOLD`, M266; before it, over a band of the keys). The sprites kept stand for their kind's fish between them; a school followed keeps all
 * its sprites, so it is never lost from the view.
 */
export const FISH_COVER = { most: 0.5, told: 30, toldPx: 24, easeS: 0.3 } as const;


/**
 * How a hunt draws a fish for a moment (M251, `habitats/hunts.ts`): its foot drawn `blend` of the way from where it
 * swims to `at` (so a hunt takes it from its own swimming and gives it back with no jump), facing `right` where given,
 * its width times `scaleX` and its whole size times `scale`, its opacity times `alpha`, flashed `flash` toward
 * `FLASH_COLOR`. Only its drawing: where it swims, and the school it swims in, are its own throughout.
 */
export interface FishPose {
  at: THREE.Vector3;
  blend: number;
  right?: boolean;
  scaleX?: number;
  scale?: number;
  alpha?: number;
  flash?: number;
}

/**
 * How the reef's interactions steer a fish for a while (M265, `habitats/reefLife.ts`): its foot swum `blend` of the way
 * from its school's place to (x, y, z), never into the floor, its way `face` where given (else the way it moves), tilted
 * nose down `tilt` radians, and drawn at least `hold` whatever the level of detail (so a fish in an interaction is not
 * lost halfway through it, the brief's section 10). Unlike a hunt's pose it moves the fish itself: it swims there, its
 * school's place under it all the while, and swims back as the blend falls.
 */
export interface FishSteer {
  x: number;
  y: number;
  z: number;
  blend: number;
  tilt?: number;
  face?: readonly [number, number];
  hold?: number;
}

/**
 * Each fish's own ways among its school on the Reef (M265, the brief's section 5; `FishSchools.individual`), JUDGED, no
 * reef fish's measured: its pace `paceSwing` either way of its school's over `paceS` (a little quicker or slower than
 * its neighbors, eased); its room `space[0]` to `space[1]` of `REPEL_BL` (spacing that varies); in every `leadS`, at
 * `leadOdds`, the nearest of its schoolmates followed (`FOLLOW_WEIGHT`); in a school of `splitLeast` or more, in every
 * `splitS` at `splitOdds`, a third of it drawn `splitBL` off its middle for `splitForS` and back (a few apart a while,
 * rejoining); and a fish whose place lies over rock rising `obstacleBL` of its lengths over its school's point turned
 * aside from it at `aroundBLPerS`, so a school parts round an outcrop, while within `aroundMostBL` of its school's middle
 * (M265's: unbounded, over a rock's flat top it was pushed every frame, 21 of its lengths off its school in five minutes).
 */
export const SCHOOL_LIFE = { paceSwing: 0.12, paceS: 7, space: [0.85, 1.15], leadS: 6, leadOdds: 0.35, splitLeast: 6, splitS: 20, splitOdds: 0.3, splitBL: 2.5, splitForS: 6, obstacleBL: 0.8, aroundBLPerS: 0.6, aroundMostBL: 3 } as const;

/** A fish as a hunt may take it (M251): its name, its kind and phase, where it is drawn, its size, the way it faces, and how much of it is drawn. */
export interface PosableFish {
  id: string;
  species: string;
  kind: number;
  phase: 0 | 1;
  at: THREE.Vector3;
  width: number;
  height: number;
  right: boolean;
  shown: number;
  sprite: Sprite;
  /** Its school's seed, followed or not (`hold`). */
  school: number;
  /** Its kind's mean total length at its phase, mm (the simulation's, not as drawn). */
  tlMm: number;
  /** How far its school is out feeding by day, 0 to 1 (its daylight times how awake it is, as its school's dives were: M265). */
  feeds?: number;
  /** How awake its school is, 0 resting to 1. */
  awake?: number;
  /** How much of it is drawn before its sheltered dimming (`shown` is after it): the interactions' share, for a sheltered fish dimmed at night is still a fish there (review M267's). */
  drawn?: number;
}

/**
 * A share drawn, 0 to 1, faded toward `target` by `seconds` at a whole change over `fadeS`; at once where the seconds are
 * not given (M266: a fish's coming or leaving, `RECONCILE.fadeS`, and its level of detail, `LOD_HOLD.fadeS`).
 */
function fadeToward(share: number, target: number, seconds: number, fadeS: number): number {
  return Number.isFinite(seconds) ? Math.min(1, Math.max(0, share + Math.sign(target - share) * Math.max(0, seconds) / fadeS)) : target;
}

/** A part of the canvas: its left, right, foot and top, in normalized device coordinates. */
export type Visible = readonly [number, number, number, number];
const WHOLE: Visible = [-1, 1, -1, 1];

/**
 * A fish as drawn: its school, its phase, its place now and off its school's point, the way it last moved and the way
 * it swims, scene units (none until first placed), and the way it faces on screen (none until first drawn).
 */
interface Drawn {
  /** Its name, the same from day to day whatever school it swims in (`f${number}`; a terminal male's `${seed}.1.${n}`, his school's and his post's): a hunt's handle on it (M251), and a follow's (M266). */
  id: string;
  key: number;
  school: number;
  phase: 0 | 1;
  at: THREE.Vector3 | null;
  off: [number, number] | null;
  moved: THREE.Vector3;
  /** The way it swims on the level, scene units a second: its school's middle's way and its own swim about it (M159). */
  way: THREE.Vector3;
  right: boolean | null;
  /** Seconds since it last turned about (`TURN_HOLD_S`). */
  held: number;
  /** The floor it keeps over as drawn, eased toward the highest about it (`RISE_BL_PER_S`), none until first placed. */
  floor: number | null;
  /** How much of it is drawn as it comes into the drawing or leaves it, 0 to 1 (M266, `RECONCILE.fadeS`). */
  fade: number;
  /** Whether it was in view when last fitted (M266: one out of view is the first to leave). */
  seen: boolean;
  /** Its school's fish it draws (none for a male), or the male's post among his school's (`patrol`). */
  member: Member | null;
  post: number;
  /** Whether the level of detail draws it (M266: kept until the level moves past its key by `LOD_HOLD.margin`), and how much of it, eased over `LOD_HOLD.fadeS`. */
  lodOn: boolean;
  lod: number;
}

/**
 * How far past square to the camera a fish swims the other way before it turns about, the sine of the angle (M159, the
 * user's report: "a bunch of fish still move backwards in the reef screen. they should almost always be facing the same
 * direction they are traveling"). It faces the way it swims (its school's middle's way and its own heading's swim about
 * it, `Drawn.way`), not the way its place last moved: its place's moves carry its school's jostling (two set apart, one
 * drawn in from the shore), which turned it about on a wobble (M153, the user's report: "some sea creatures flicker by
 * turning left and right rapidly"); M153 then held it to the way it faced until it had moved its own length the other
 * way, and on the Reef at noon a fish moved against the way it faced 47% of the time (MEASURED, M159, 64 fish over 60 s
 * at 60 frames a second on a phone held upright). Its heading turns smoothly (`schooling.ts`'s `TURN_PER_S`), so it is
 * turned once its swim is 15° past square the other way (JUDGED: no wobble about square turns it back and forth).
 */
export const FACE_SIN = Math.sin((15 * Math.PI) / 180);
/**
 * The least time between a fish's turns, seconds (M159): a milling fish's heading swings back past square within a
 * second, and at `FACE_SIN` alone the whole tank's fish on a phone at noon turned 3,093 times in 20 s among 400, 498 of
 * them back within a second of the last (MEASURED, `tests/fishFacing.test.ts`), the flicker of M153's report; held a
 * second, none (JUDGED: a turn seen, then the fish's way).
 */
const TURN_HOLD_S = 1;

/**
 * How fast the floor a fish keeps over follows the reef under it, its lengths a second (M159, the user's report: "a many of them move vertically in a choppy rapid manner"): set at once to
 * its height over the floor under it, a fish crossing an outcrop's edge jumped up or down by the outcrop's height in a
 * frame, and its school's dives to feed (`feeding`) dropped it 1.2 lengths in about half a second; the fastest 1% of its
 * moves up or down were 2.7 of its lengths a second, the fastest 10 (MEASURED, M159, as `FACE_SIN`'s). Now on the Reef it
 * keeps over the highest floor within `Roam.envelopeM` of it, so it rises ahead of an outcrop before it reaches it (the
 * map's and the whole tank's floors have no outcrops, and read so on their stretched slopes it held more schools under the
 * surface), that floor
 * eased no faster than it forages (`FORAGE_BL_PER_S`, JUDGED) and `CLIMB` as it swims on; never into the floor under it.
 * Its school's own rise and fall (sheltering, coming out, feeding) are their own smooth curves.
 */
const RISE_BL_PER_S = FORAGE_BL_PER_S;
/**
 * How much more that floor may rise or sink for each unit the fish swims on the level (JUDGED: a climb of about 27°), so
 * a school carried fast over its commute at the game's own speed keeps over the floor.
 */
const CLIMB = 0.5;
/**
 * The fastest an interaction's fish rises or sinks as drawn, its lengths a second (M265; JUDGED: under M159's 2.25 at
 * the 99th centile, `tests/reefFishMotion.test.ts`, its paths' own legs kept to 1.8 at their quickest, `reefLife.ts`).
 */
const STEER_RISE_BL_PER_S = 2;
/** The fastest an interaction's fish crosses the reef as drawn, its lengths a second (M265; JUDGED: a fright's dart, a few lengths in half a second). */
const STEER_BL_PER_S = 8;
/** How awake a roaming school is before its fish lean wholly its way, a share (JUDGED: `schooling.ts`'s pull of a fish toward its school's middle, 0.35, so below it the pull holds them). */
const ROAM_LEAN_AWAKE = 0.35;

export class FishSchools {
  readonly group = new THREE.Group();
  private schools: School[] = [];
  /**
   * Told of each bite where a school of a kind that grazes reaches the foot of a feeding dive by day (M173): its kind,
   * the floor point under one of its fish, and the bite's own key; none told where none listens.
   */
  onBite: ((kind: number, x: number, y: number, z: number, key: number) => void) | null = null;
  private drawnFish: Drawn[] = [];
  /** The school followed, by its seed: all its sprites drawn whatever the level of detail (`hold`). */
  private held: number | null = null;
  /** The fish followed, by its name (M266, `holdFish`): kept through a day's new counts while any fish is, and drawn whatever the level of detail. */
  private heldFish: string | null = null;
  /** The next fish's number (M266, `Member.id`): each new fish's its own, in the order they come. */
  private nextFish = 0;
  /** Whether a frame of its own time has been fitted yet (M266: before it, new fish are drawn whole, not faded in). */
  private timed = false;
  /** The fish a hunt draws for now, by name (M251, `pose`); kept by name across a new day's schools. */
  private readonly poses = new Map<string, FishPose>();
  /** The fish the reef's interactions steer for now, by name (M265, `steer`). */
  private readonly steers = new Map<string, FishSteer>();
  /** The fish let go by an interaction, still rising or sinking back to their school's height at its pace (M265). */
  private readonly settling = new Set<string>();
  /** The kinds whose feeding the interactions draw, fish by fish (M265): their schools' dives to the reef left off. */
  private ownFeeding: ReadonlySet<number> = new Set();
  /** Whether each fish has its own ways among its school (`SCHOOL_LIFE`, M265): the Reef's alone. */
  private individual = false;
  /** A school split now by the developer (the brief's section 13), by its seed: its swimming's seconds as it began. */
  private readonly splitAt = new Map<number, number>();
  /** Each fish as last drawn (`fit`), for a hunt to find its fish among (M251). */
  private lastDrawn: PosableFish[] = [];
  /** The share of the fish drawn at the camera's level of detail (`FISH_COVER`), 1 all. */
  private keep = 1;
  private readonly batch: FishBatch;
  private readonly castersNow: Caster[] = [];
  private readonly rects: Map<Sprite, [number, number, number, number]>;
  /** Each kind's mean lengths, mm, as last set. */
  private lengths: KindCounts['lengthMm'][] = [];
  /** The sprites' count by kind, phase and island, as last built. */
  private built = '';
  /** The hour last swum to, for a jump of it (`JUMP_HOURS`). */
  private lastHour = Number.NaN;
  private readonly from = new THREE.Vector3();
  private readonly to = new THREE.Vector3();
  private readonly middle = new THREE.Vector3();
  /** The tallest picture's height to its length, so a fish's top is kept under the sea. */
  private readonly tallest: number;

  /**
   * @param place a column's center on the reef, scene units
   * @param floorAt the floor's height under a point, scene units
   * @param surfaceY the sea's surface, scene units: a fish whose place would be over land is drawn in toward its school's point
   * @param reefOf each reef column's island
   */
  constructor(
    private readonly kinds: readonly FishKind[],
    private readonly cells: number,
    private readonly cellKm: number,
    private readonly reefOf: ReadonlyMap<number, number>,
    private readonly place: (column: number, out: THREE.Vector3) => THREE.Vector3,
    private readonly floorAt: (x: number, z: number) => number,
    private readonly surfaceY: number,
    /** Fish a sprite stands for, and its size against the map's (the exhibit's larger, M94). */
    private readonly perSprite = FISH_PER_SPRITE,
    private readonly sizeScale = 1,
    /**
     * Whether water must be deep enough to hold a fish to be swum in: its floor at least the tallest sprite's
     * height, at its school's longest phase, under the surface (M94, the exhibit's: its stretched shallows run
     * from the coast, and a fish laid where the water was shallower than it is tall was drawn in the floor, 30 of
     * 332 on the first day). Elsewhere water is water.
     */
    private readonly fitWater = false,
    /** Whether a fish is hidden behind what stands nearer it (`FishBatch`'s; a habitat scene's, M110). */
    depthTest = false,
    /**
     * The highest `floorAt` gives anywhere, or more (M122, the Reef scene's, whose water reaches no shore): where it is
     * under the water a school needs, every place is wet, and no shore is searched for.
     */
    private readonly highest: () => number = () => Infinity,
    /**
     * A box no fish is drawn outside, across and along (its `min` and `max` x and z), or none (M127, Maui Nui Reef's
     * camera box, the user's word: "make sure all the sea creatures locations are within that as well"; its review's: on
     * the slowest Watch One Day a school's lone fish drifted 4.88 m past it as its school commuted, the school's pull on
     * a fish alone weak against its travel).
     */
    private readonly keepIn: { min: readonly number[]; max: readonly number[] } | null = null,
    /**
     * Each school's middle roaming the reef in view by day (M159, the Reef's, the user's word: "their routes need to be
     * bigger and traverse much more of the reef"): a loop through `points` of the reef's columns, at `blPerS` of its
     * lengths a second, its fish leaning `lean` its way; within `rangeM` of its shelter where its kind's home range is
     * measured smaller than the view (the roi's), else anywhere on the reef in view. None elsewhere.
     */
    private readonly roam: Roam | null = null,
  ) {
    const sprites = kinds.flatMap((k) => (k.terminal ? [k.initial, k.terminal] : [k.initial]));
    this.tallest = Math.max(...sprites.map((s) => s.height / s.width));
    const atlas = fishAtlas(sprites);
    this.rects = atlas.rects;
    this.batch = new FishBatch(atlas.texture, depthTest);
    this.group.add(this.batch.mesh);
  }

  /** The highest floor within `Roam.envelopeM` of each cell of `Roam.envelopeCell`, read once a cell (M159's review: read round each fish each frame, the Reef's fish cost 3.8 times as much). */
  private readonly envelope = new Map<string, number>();

  /**
   * The highest floor within `Roam.envelopeM` of a point, read at its cell's middle over a disc (its middle, and eight
   * points at half and at the whole of the radius: M159's review found a ring alone missed an outcrop under its middle);
   * none where the schools do not roam.
   */
  private envelopeAt(x: number, z: number): number {
    const r = this.roam?.envelopeM ?? 0;
    if (r <= 0) return -Infinity;
    const c = this.roam?.envelopeCell ?? 1;
    const corner = (i: number, k: number): number => {
      const key = `${i},${k}`;
      const known = this.envelope.get(key);
      if (known !== undefined) return known;
      const [cx, cz] = [i * c, k * c];
      let top = this.floorAt(cx, cz);
      for (const d of [r / 2, r]) for (let a = 0; a < 8; a++) top = Math.max(top, this.floorAt(cx + d * Math.cos((a * Math.PI) / 4), cz + d * Math.sin((a * Math.PI) / 4)));
      this.envelope.set(key, top);
      return top;
    };
    // Between its four corners, so it changes smoothly as a fish swims across a cell.
    const [u, v] = [x / c, z / c];
    const [i, k] = [Math.floor(u), Math.floor(v)];
    const [fu, fv] = [u - i, v - k];
    return (corner(i, k) * (1 - fu) + corner(i + 1, k) * fu) * (1 - fv) + (corner(i, k + 1) * (1 - fu) + corner(i + 1, k + 1) * fu) * fv;
  }

  /** Forgets the floor read round each cell, for a floor changed under the fish (the Reef's outcrops laid). */
  floorChanged(): void {
    this.envelope.clear();
  }

  /** Fish a sprite of a kind stands for: its own (`FishKind.perSprite`) or the view's. */
  private perSpriteOf(kind: number): number {
    return this.kinds[kind]?.perSprite ?? this.perSprite;
  }

  /** A fish's mean total length for its kind and phase, mm, as the day's data gives it (the simulation's, not as drawn). */
  private mmOf(kind: number, phase: 0 | 1): number {
    const mm = this.lengths[kind];
    return (phase === 0 ? mm?.initial : mm?.terminal) ?? 0;
  }

  /** A fish's length, scene units, for its kind and phase. */
  private lengthOf(kind: number, phase: 0 | 1): number {
    return this.mmOf(kind, phase) * UNITS_PER_MM * this.sizeScale;
  }

  /**
   * The schools for each kind's fish on a day: on each island, its initial
   * phase in schools of `swim.school` sprites (at least one where it
   * has any fish), its terminal males shared among them. Built again only
   * when their numbers change.
   */
  setCounts(counts: readonly KindCounts[]): void {
    this.lengths = counts.map((c) => c.lengthMm);
    const plan = counts.map((c, k) => {
      const per = this.perSpriteOf(k);
      return [spritesByIsland(c.initial, c.byIsland, per), spritesByIsland(c.terminal, c.terminalByIsland ?? c.byIsland, per)];
    });
    const key = JSON.stringify(plan);
    if (key === this.built) return;
    // Drawn whole at once until the scene has been on screen; every later change faded (M266).
    const first = !this.timed;
    this.built = key;
    const columns = new Map<number, number[]>();
    for (const [column, island] of [...this.reefOf].sort((a, b) => a[0] - b[0])) columns.set(island, [...(columns.get(island) ?? []), column]);
    // A column whose floor, as drawn at its middle, is under the sea: a shore column's can stand above it; deep
    // enough for a kind's own longest fish where it must hold one (M146's review: deep enough for the longest of every
    // kind, the ulua's 1.24 units moved 122 of the other kinds' 156 schools off 177 shallow columns; the parrotfish and
    // the roi, set before by the kala's length, the longest then, now find some shallower reef of their own: 59 of the
    // 156 routes differ from before M146, MEASURED).
    const wetReefs = new Map<number, Map<number, number>>();
    const wetReefOf = (k: number): Map<number, number> => {
      const longest = Math.max(this.lengthOf(k, 0), this.lengthOf(k, 1));
      const sea = this.surfaceY - (this.fitWater ? this.tallest * longest : 0);
      let wet = wetReefs.get(sea);
      if (!wet) {
        wet = new Map([...this.reefOf].filter(([column]) => {
          this.place(column, this.from);
          return this.floorAt(this.from.x, this.from.z) < sea;
        }));
        wetReefs.set(sea, wet);
      }
      return wet;
    };
    const kept = this.schools;
    const drawnBefore = new Map(this.drawnFish.map((d) => [d.id, d]));
    this.schools = [];
    // The fish that leave first where the drawing must lose some (M266): one a hunt has eaten (hidden already), then one
    // not followed, then one out of view or thinned, then the newest; the simulation names no fish that died, so none is
    // singled out.
    const leavesFirst = (a: Member, b: Member): number => {
      const rank = (m: Member): number[] => {
        const id = memberId(m);
        const d = drawnBefore.get(id);
        // The one followed after any in view (review 31: ranked after them, it left first while out of view behind the
        // card); in view meaning drawn there, not one the level of detail thinned (review 31).
        return [this.poses.get(id)?.alpha === 0 ? 0 : 1, id === this.heldFish ? 1 : 0, d?.seen && d.lod > 0 ? 1 : 0, -m.id];
      };
      const [x, y] = [rank(a), rank(b)];
      for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return (x[i] as number) - (y[i] as number);
      return 0;
    };
    plan.forEach(([initial = [], terminal = []], k) => {
      const kind = this.kinds[k] as FishKind;
      const males = kind.terminal ? terminal : terminal.map(() => 0);
      const islands = Math.max(initial.length, males.length, ...kept.filter((x) => x.kind === k).map((x) => x.island + 1));
      for (let island = 0; island < islands; island++) {
        const reef = columns.get(island) ?? [];
        const n = initial[island] ?? 0;
        const m = males[island] ?? 0;
        const base = (k * 64 + island) * 4096;
        const before = kept.filter((x) => x.kind === k && x.island === island).sort((a, b) => a.seed - b.seed);
        if (reef.length === 0) continue;
        // As many schools as its fish make, kept as they were while that stays within reach (M266: a fish's coming or
        // going split a school or joined two, regrouping every fish of its kind there; `RECONCILE.schoolMargin`).
        const ideal = n / kind.swim.school;
        const had = before.filter((x) => x.fish.some((f) => !f.leaving)).length;
        const count = n + m === 0 ? 0 : had > 0 && Math.abs(ideal - had) < 0.5 + RECONCILE.schoolMargin ? had : Math.max(1, Math.round(ideal));
        // The `g`th school's share of `all` fish: as even as can be, one more each in the first `all % count`.
        const shareOf = (all: number, g: number): number => Math.floor(all / count) + (g < all % count ? 1 : 0);
        const pool: { member: Member; from: School }[] = [];
        const planned: School[] = [];
        for (let g = 0; g < count; g++) {
          const seed = base + g;
          // A school kept from the day before keeps its fish where they are.
          const old = before.find((x) => x.seed === seed);
          const fish = old?.fish ?? [];
          const rng = old?.rng ?? noiseStream(seed);
          // Its shelter and its feeding ground in water: the commute walks the island's wet reef where it has any.
          const wetOf = wetReefOf(k);
          const wetReef = reef.filter((column) => wetOf.has(column));
          const [starts, walk] = wetReef.length > 0 ? [wetReef, wetOf] : [reef, this.reefOf];
          const start = starts[Math.floor(hash3(seed, 0, 0) * starts.length)] as number;
          const [near, far] = kind.swim.commuteM;
          const km = ((near + (far - near) * hash3(seed, 0, 1)) * kind.swim.wander) / 1000;
          const route = old?.route ?? routeOf(reefPath(this.cells, this.cellKm, walk, start, km, seed), this.cells);
          const loop = old?.loop !== undefined ? old.loop : this.loopOf(kind, [...walk.keys()].sort((a, b) => a - b), route.path[0] as number, seed);
          const school: School = {
            seed, kind: k, island, route, drawn: drawnRoute(route, this.place), fish, males: old?.males ?? 0, malesLeaving: old?.malesLeaving ?? 0, rng,
            phase: hash3(seed, 0, 2), seconds: old?.seconds ?? 0, share: old?.share ?? 0, awake: old?.awake ?? 0, fresh: old?.fresh ?? true,
            travelling: old?.travelling ?? 0, chasing: old?.chasing ?? false, loop, roamed: old?.roamed ?? 0, lastMiddle: old?.lastMiddle ?? null,
            daylight: old?.daylight ?? 0,
          };
          planned.push(school);
          // Its fish beyond its share taken out to be given to another of the kind's schools there, or to leave.
          const want = shareOf(n, g);
          const alive = fish.filter((f) => !f.leaving).sort(leavesFirst);
          for (const f of alive.slice(0, Math.max(0, alive.length - want))) pool.push({ member: f, from: school });
          // Its males: the last ones leave, fading; one wanted again turns back (M266).
          const wantMales = shareOf(m, g);
          const malesAlive = school.males - school.malesLeaving;
          if (wantMales < malesAlive) school.malesLeaving += malesAlive - wantMales;
          else if (wantMales > malesAlive) {
            const back = Math.min(school.malesLeaving, wantMales - malesAlive);
            school.malesLeaving -= back;
            school.males += wantMales - malesAlive - back;
          }
        }
        // A school no longer wanted: its fish taken out to join the others, its males fading, the school drawn on until
        // it has none left (M266: it had gone at once with every fish in it).
        for (const old of before.filter((x) => x.seed - base >= count)) {
          for (const f of old.fish.filter((x) => !x.leaving)) pool.push({ member: f, from: old });
          old.malesLeaving = old.males;
          planned.push(old);
        }
        // A fish of the pool swum over to a school (its identity kept), its place about the school's middle its own.
        const moveTo = (member: Member, from: School, school: School): void => {
          if (from === school) return;
          from.fish.splice(from.fish.indexOf(member), 1);
          const a = 2 * Math.PI * school.rng();
          const r = 2 * Math.sqrt(school.rng());
          Object.assign(member, { x: r * Math.cos(a), z: r * Math.sin(a) });
          school.fish.push(member);
          const d = drawnBefore.get(memberId(member));
          if (d) d.off = null;
        };
        // Each school short of its share given fish from the pool, which swim over to it; then new ones fading in about
        // its middle; the pool's rest leave, fading where they are.
        pool.sort((a, b) => leavesFirst(b.member, a.member));
        planned.forEach((school, g) => {
          if (g >= count) return;
          const want = shareOf(n, g);
          let alive = school.fish.filter((f) => !f.leaving).length;
          while (alive < want && pool.length > 0) {
            const { member, from } = pool.shift() as { member: Member; from: School };
            moveTo(member, from, school);
            alive++;
          }
          // Each new one's level-of-detail key the first of its school's places none staying holds (review 31: its school's
          // length, the gone not yet pruned among it, gave a key by the frames' timing, and could repeat a kept one's).
          const held = new Set(school.fish.filter((f) => !f.leaving).map((f) => f.key));
          while (alive < want) {
            const a = 2 * Math.PI * school.rng();
            const r = 2 * Math.sqrt(school.rng());
            let place = 0;
            while (held.has(hash3(school.seed, place, 105))) place++;
            const key = hash3(school.seed, place, 105);
            held.add(key);
            school.fish.push({ id: this.nextFish++, x: r * Math.cos(a), z: r * Math.sin(a), hx: Math.cos(a + 1), hz: Math.sin(a + 1), leaving: false, key });
            alive++;
          }
        });
        // The pool's best kept in place of a kept school's last to keep, where it ranks above it (M266's own test: the
        // fish followed, or one in view, in a school no longer wanted had left while a kept school kept one unseen).
        while (pool.length > 0) {
          let worst: { member: Member; school: School } | null = null;
          const pooled = new Set(pool.map((x) => x.member));
          for (const school of planned.slice(0, count)) for (const f of school.fish) if (!f.leaving && !pooled.has(f) && (!worst || leavesFirst(f, worst.member) < 0)) worst = { member: f, school };
          const best = pool[0] as { member: Member; from: School };
          if (!worst || leavesFirst(worst.member, best.member) >= 0) break;
          pool.shift();
          worst.member.leaving = true;
          moveTo(best.member, best.from, worst.school);
        }
        for (const { member } of pool) member.leaving = true;
        this.schools.push(...planned.filter((x) => x.fish.length + x.males > 0));
      }
    });
    this.drawnFish = this.drawnOf(drawnBefore, first);
  }

  /**
   * Each school's fish as drawn, in the order `swim` lays them (each school's fish, then its males), each one's drawing
   * kept by its name (M266: a fish's name its own, not its place in its school; a male's his school's and his post's).
   */
  private drawnOf(before: ReadonlyMap<string, Drawn>, whole = false): Drawn[] {
    const out: Drawn[] = [];
    this.schools.forEach((s, school) => {
      const add = (id: string, phase: 0 | 1, key: number, member: Member | null, post: number): void => {
        const old = before.get(id);
        if (old) {
          Object.assign(old, { school, member, post });
          out.push(old);
        } else out.push({ id, key, school, phase, at: null, off: null, moved: new THREE.Vector3(-1, 0, 0), way: new THREE.Vector3(), right: null, held: TURN_HOLD_S, floor: null, fade: whole ? 1 : 0, seen: false, member, post, lodOn: key <= this.keep, lod: key <= this.keep ? 1 : 0 });
      };
      for (const f of s.fish) add(memberId(f), 0, f.key, f, 0);
      for (let n = 0; n < s.males; n++) add(`${s.seed}.1.${n}`, 1, hash3(s.seed, 4096 + n, 105), null, n);
    });
    return out;
  }

  /**
   * A school's roaming loop (`Roam`): from its shelter's column through `points` more of the reef's columns (any
   * island's: each kind's home range, all but the roi's, is larger than the view, and an island's own patches of it are
   * a median 14.6 m across, MEASURED, M159), each by its own draw, within its kind's range of the shelter where it has
   * one, taken round the shelter by their bearing so the loop does not cross itself; none where it does not roam or no
   * other column is in range.
   */
  private loopOf(kind: FishKind, columns: readonly number[], home: number, seed: number): Loop | null {
    if (!this.roam) return null;
    const at = (column: number): [number, number] => {
      this.place(column, this.from);
      return [this.from.x, this.from.z];
    };
    const [hx, hz] = at(home);
    const range = this.roam.rangeM(kind);
    const near = columns.filter((c) => c !== home && (range === null || Math.hypot(at(c)[0] - hx, at(c)[1] - hz) <= range));
    if (near.length === 0) return null;
    const picked = new Set<number>();
    for (let i = 0; picked.size < Math.min(this.roam.points, near.length) && i < 64; i++) picked.add(near[Math.floor(hash3(seed, i, 159) * near.length)] as number);
    const points = [[hx, hz] as [number, number], ...[...picked].map(at).sort((a, b) => Math.atan2(a[1] - hz, a[0] - hx) - Math.atan2(b[1] - hz, b[0] - hx))];
    const legs = points.map((p, i) => {
      const q = points[(i + 1) % points.length] as [number, number];
      return Math.hypot(q[0] - p[0], q[1] - p[1]);
    });
    const total = legs.reduce((a, b) => a + b, 0);
    return total > 0 ? { points, legs, total } : null;
  }

  /**
   * Each school where its commute has it at `hour` (solar hours since
   * midnight) of a day `dayLengthHours` long, its fish moved on by `seconds`
   * of swimming (the player's own seconds, the animation clock's: on while
   * the game is paused, M95; none in a frame of no time) in steps of at most
   * `STEP_S`. `calm` (a player who asks for less
   * motion) keeps each school still within itself, its commute swum all the
   * same (the user's word, M83: "have them move between the two").
   */
  swim(hour: number, dayLengthHours: number, seconds: number, calm = false): void {
    // A frame's swimming capped, so a quick speed costs no more than `MAX_SWIM_S` of steps; and a frame timed before
    // the last (the browser's first frame stamped before the page's clock began, M234) none, as a frame of no time: its
    // negative seconds put every fish's place and height at NaN, and each frame steps both from the last, so they stayed
    // there; and NaN seconds as none too (M234's review).
    const run = seconds > 0 ? Math.min(seconds, MAX_SWIM_S) : 0;
    const steps = run > 0 && !calm ? Math.ceil(run / STEP_S) : 0;
    // The hour's move since the last frame, the shorter way round the clock.
    const moved = Math.abs(((((hour - this.lastHour) % 24) + 36) % 24) - 12);
    const jumped = Number.isFinite(this.lastHour) && moved > JUMP_HOURS;
    this.lastHour = hour;
    let d = 0;
    const highest = this.highest();
    this.schools.forEach((s) => {
      const bl = this.lengthOf(s.kind, 0);
      // Where the hour has its middle: making its way out and back over the whole day (`dayShare`), at its shelter
      // at midnight and its ground at noon; risen from its shelter and brightened as far as it has come out; and
      // travelling most about sunrise and sunset (`dayHurry`): so nothing about it changes at once (the user's
      // word, M91: "they pop in and out when it goes between day and night").
      const profile = (this.kinds[s.kind] as FishKind).swim.day;
      let share = dayShare(hour, dayLengthHours);
      // Awake all day where its kind is active at both its grounds (M146: the ulua), else as far as it has come out.
      let awake = (this.kinds[s.kind] as FishKind).swim.awakeAtNight ? 1 : share;
      let hurry = dayHurry(hour, dayLengthHours);
      if (jumped && !s.fresh) s.chasing = true;
      if (s.chasing && !profile) {
        // After a jump of the hour, swum to where it has the school now, a whole change over `CHASE_S` at least.
        const most = run / CHASE_S;
        const [wantShare, wantAwake] = [share, awake];
        share = s.share + Math.max(-most, Math.min(most, wantShare - s.share));
        awake = s.awake + Math.max(-most, Math.min(most, wantAwake - s.awake));
        hurry = Math.min(1, 10 * Math.abs(wantShare - share));
        if (Math.abs(wantShare - share) < 1e-3 && Math.abs(wantAwake - awake) < 1e-3) s.chasing = false;
      }
      if (profile) {
        // A kind with its own day (M97): toward where its hour has it, a whole change over `easeS` of swimming at
        // least; travelling as long as it is on its way.
        const want = profile.at(hour, dayLengthHours);
        const most = s.fresh ? 1 : run / profile.easeS;
        share = s.share + Math.max(-most, Math.min(most, want.out - s.share));
        awake = s.awake + Math.max(-most, Math.min(most, want.awake - s.awake));
        hurry = Math.min(1, 10 * Math.abs(want.out - share));
      }
      s.fresh = false;
      s.travelling = share === s.share ? 0 : Math.sign(share - s.share) * hurry;
      // The way the school is bound on the level as it travels: along its route, out or home, leaning to it as
      // much as it is travelling.
      const at = pointOnRoute(s.drawn, share);
      this.place(at.from, this.from);
      this.place(at.to, this.to);
      this.middle.lerpVectors(this.from, this.to, at.along);
      let travel: [number, number] = [0, 0];
      if (share !== s.share && this.from.distanceTo(this.to) > 1e-9) {
        const way = this.to.clone().sub(this.from);
        const lean = ((share > s.share ? 1 : -1) * hurry) / Math.hypot(way.x, way.z);
        travel = [way.x * lean, way.z * lean];
      }
      // Roaming its loop by day, as far round it as it has swum, out from its shelter as far as it is awake (M159).
      if (s.loop && this.roam) {
        if (!calm) s.roamed += run * this.roam.blPerS * bl * awake;
        const [home, here] = [onLoop(s.loop, 0), onLoop(s.loop, s.roamed)];
        this.middle.x += (here.x - home.x) * awake;
        this.middle.z += (here.z - home.z) * awake;
        // Its middle kept `insetM` inside the box its fish are kept in, so its school is never pressed against it (M159's
        // review: the loop's corner and its commute's offset together took it up to 1 m past).
        if (this.keepIn) {
          const [{ min, max }, m] = [this.keepIn, this.roam.insetM];
          this.middle.x = Math.min(Math.max(this.middle.x, (min[0] as number) + m), (max[0] as number) - m);
          this.middle.z = Math.min(Math.max(this.middle.z, (min[2] as number) + m), (max[2] as number) - m);
        }
        this.middle.y = this.floorAt(this.middle.x, this.middle.z);
        // Its fish leaning its way wholly once it is a third awake (`ROAM_LEAN_AWAKE`), not as far as it is awake: leaning
        // short of their middle's way, they drift ahead of it (M159's second review: at 8 am, three quarters awake, 89
        // lengths in eight minutes, pressed against the box); the commute's lean beside it held to a whole.
        const lean = this.roam.lean * Math.min(1, awake / ROAM_LEAN_AWAKE);
        travel = [travel[0] + here.wx * lean, travel[1] + here.wz * lean];
        const t = Math.hypot(travel[0], travel[1]);
        if (t > 1) travel = [travel[0] / t, travel[1] / t];
      }
      // Its middle's way since it was last drawn, scene units a second (none while the game is paused).
      const middleWay = s.lastMiddle && run > 0 ? this.middle.clone().sub(s.lastMiddle).multiplyScalar(1 / run) : new THREE.Vector3();
      (s.lastMiddle ??= new THREE.Vector3()).copy(this.middle);
      s.share = share;
      s.awake = awake;
      // At night it mills slowly where it shelters, its males' beats slowed as much; by day it forages, or keeps to
      // its commute's pace as it travels, swimming together as much.
      const day = FORAGE_BL_PER_S + (COMMUTE_BL_PER_S - FORAGE_BL_PER_S) * hurry;
      const pace = NIGHT_BL_PER_S + (day - NIGHT_BL_PER_S) * awake;
      const orient = FORAGE_ORIENT_BL + (ORIENT_BL - FORAGE_ORIENT_BL) * hurry;
      if (this.individual && !calm) this.ownWays(s, bl);
      for (let i = 0; i < steps; i++) stepSchool(s.fish, run / steps, pace, travel, s.rng, orient);
      const beat = NIGHT_BL_PER_S / FORAGE_BL_PER_S;
      const before = s.seconds;
      if (!calm) s.seconds += run * (beat + (1 - beat) * awake);
      // Dropping to the reef to feed in its beats by day, as its fish bite only between sunrise and sunset (Tricas and
      // Boyle 2021, `fishKinds.ts`): from none halfway out, at sunrise, to all its beats a quarter of the way further
      // (JUDGED, so it never starts at once; 8.4 hours on Maui Nui's shortest day), and back to none at sunset. MEASURED
      // in M91's reviews over ten game days of four seconds at the game's own speed: more than halfway down in 9.1% of
      // school-frames, where kept from feeding as much as it travelled 3.6% and with the hour's swims 12.9% (fed
      // whenever it was out, it dropped to feed before sunrise and after sunset); on five, `fishSwim.test.ts`. Since
      // M159, its bouts 6 s of every 18 and eased (`feeding`), its fish over their eased floor: 6.7% at the game's own
      // speed, 9.6% at a quarter of it.
      const daylight = Math.min(1, Math.max(0, 4 * (share - 0.5)));
      s.daylight = daylight;
      // Its kind's feeding drawn fish by fish where the interactions draw it (M265), its school's dives left off.
      const ownFeeds = this.ownFeeding.has(s.kind);
      // Its kind's own cruise and bites where it has them (M169).
      const swimOf = (this.kinds[s.kind] as FishKind).swim;
      const cruiseBL = swimOf.cruiseBL ?? CRUISE_BL;
      const cruise = cruiseBL - (swimOf.grazes === false || ownFeeds ? 0 : daylight * (cruiseBL - FEED_BL) * feeding(s.seconds, s.phase));
      const lift = REST_BL + (cruise - REST_BL) * awake;
      const sea = this.surfaceY - (this.fitWater ? this.tallest * Math.max(bl, this.lengthOf(s.kind, 1)) : 0);
      const wet = (x: number, z: number): boolean => this.floorAt(this.middle.x + x, this.middle.z + z) < sea;
      const pointWet = wet(0, 0);
      const shoreless = highest < sea;
      /**
       * The share of the way to a place, (x, z) scene units off the school's point, before the first land along it
       * (searched in `SHORE_STEPS`, then by halving), so a place over land, or over water beyond a spit of it, is
       * drawn in to the shore and reached straight from the point. All of it where the point itself reads as land,
       * between a shore's columns, and where no land stands so high (`shoreless`).
       */
      const shore = (x: number, z: number): number => {
        const r = Math.hypot(x, z);
        if (shoreless || !pointWet || r === 0) return 1;
        // Searched `SHORE_GAP_BL` of its school's lengths past the place, so it is kept that far off the shoreline,
        // drawn in from none at all as the shore comes within that of it.
        const reach = 1 + (SHORE_GAP_BL * bl) / r;
        const [ex, ez] = [x * reach, z * reach];
        const sea = firstLand(ex, ez, wet);
        if (sea === null) return 1;
        return Math.max(0, Math.min(1, sea * reach - (reach - 1)));
      };
      /**
       * Lays a fish toward its place about its school's point, (x, z) scene units off it, drawn in to the shore
       * where that is over land, `rise` of its lengths over the floor; and gives the share of the way to its place
       * before the shore, `shore`'s.
       * It is carried with its school's point and swims about it at most `MOST_BL_PER_S` of its lengths a second (M88,
       * the user's word: "the fish should be moving continuously, not stop and go like it is now"; drawn straight at
       * its place, one pulled in from over land jumped). Where land comes between it and the point as the point moves,
       * which is all day (M91), it is drawn in to the shore at once if its own place is over land, and otherwise no
       * faster than another of its steps: drawn in at once wherever land lay between, a fish at a headland hopped as
       * its line to the point crossed the headland's tip (MEASURED: 43 hops of more than a length in three game days
       * at the game's own speed on the map, where 13 now; in M91's review, on its four-second day, 48 and 49 with the
       * hour's swims, none in the diorama), and drawn in no faster than it swims, fish glided over land for seconds
       * (2,276 fish-frames in those three days, where 17 now). A strip of land too thin for the search it swims
       * across.
       */
      const lay = (x: number, z: number, length: number, rise: number, swimX?: number, swimZ?: number): number => {
        const fish = this.drawnFish[d++];
        if (!fish) return 1;
        const k = shore(x, z);
        const [tx, tz] = [x * k, z * k];
        // One given to another school (M266) swims to it from where it was drawn, its place off the new one's point.
        const [px, pz] = fish.off ?? (fish.at ? [fish.at.x - this.middle.x, fish.at.z - this.middle.z] : [tx, tz]);
        const way = Math.hypot(tx - px, tz - pz);
        const most = MOST_BL_PER_S * length * run;
        const [ox, oz] = way > most ? [px + ((tx - px) * most) / way, pz + ((tz - pz) * most) / way] : [tx, tz];
        // Drawn in to the shore along its way from the point: at most another step, but at once from over land or
        // where that step would set it down on the land it crosses (M91's second review: so capped, 26 of 35
        // fish-frames over land in three game days of four seconds; MEASURED on five, 54 where 17 now); and not at
        // all while the game is paused.
        const back = shore(ox, oz);
        let [fx, fz] = [ox * back, oz * back];
        const pull = Math.hypot(ox - fx, oz - fz);
        if (pull > most && wet(ox, oz)) {
          const [cx, cz] = [ox + ((fx - ox) * most) / pull, oz + ((fz - oz) * most) / pull];
          if (wet(cx, cz)) [fx, fz] = [cx, cz];
        }
        if (run === 0 && fish.off) [fx, fz] = fish.off;
        if (this.keepIn) {
          const { min, max } = this.keepIn;
          fx = Math.min(Math.max(this.middle.x + fx, min[0] as number), max[0] as number) - this.middle.x;
          fz = Math.min(Math.max(this.middle.z + fz, min[2] as number), max[2] as number) - this.middle.z;
        }
        fish.off = [fx, fz];
        const [wx, wz] = [this.middle.x + fish.off[0], this.middle.z + fish.off[1]];
        // Over the highest floor within `Roam.envelopeM` of it where given, but never more than `DROP_BL` below its
        // school's point on the reef, and wholly under the sea (M88's review: on the map a reef 10 m deep is drawn
        // shallower than a fish's cruising height); risen or sunk toward it no faster than `RISE_BL_PER_S` and `CLIMB`,
        // and never into the floor under it (M159).
        const under = this.floorAt(wx, wz);
        const around = Math.max(under, this.envelopeAt(wx, wz));
        const top = this.surfaceY - this.tallest * length;
        const wantFloor = Math.max(around, this.middle.y - DROP_BL * bl);
        const step = RISE_BL_PER_S * length * run + CLIMB * (fish.at ? Math.hypot(wx - fish.at.x, wz - fish.at.z) : 0);
        fish.floor = fish.floor === null ? wantFloor : fish.floor + Math.max(-step, Math.min(step, wantFloor - fish.floor));
        const next = new THREE.Vector3(wx, Math.min(Math.max(fish.floor + rise * length, Math.min(under, top)), top), wz);
        // Steered by an interaction (M265): swum its blend of the way to the interaction's point, never into the floor
        // there nor out of the sea; its school's place kept under it, so it swims back as the blend falls.
        const steer = this.steers.get(fish.id);
        const b = steer ? Math.min(1, Math.max(0, steer.blend)) : 0;
        if (steer && b > 0) {
          next.x += (steer.x - next.x) * b;
          next.z += (steer.z - next.z) * b;
          // No quicker than `STEER_BL_PER_S` across from where it was drawn (a dart's burst; its way back to its school no
          // more than its school's own drawing in once the interaction lets it go, below).
          if (fish.at) {
            const [dx, dz] = [next.x - fish.at.x, next.z - fish.at.z];
            const [moved, most] = [Math.hypot(dx, dz), STEER_BL_PER_S * length * run];
            if (moved > most) [next.x, next.z] = [fish.at.x + (dx * most) / moved, fish.at.z + (dz * most) / moved];
          }
          // Kept in the box every creature is kept in (M127), as its place is.
          if (this.keepIn) {
            const { min, max } = this.keepIn;
            next.x = Math.min(Math.max(next.x, min[0] as number), max[0] as number);
            next.z = Math.min(Math.max(next.z, min[2] as number), max[2] as number);
          }
          // Risen or sunk no faster than `STEER_RISE_BL_PER_S` from where it was drawn (between its school's place and the
          // interaction's, the reef rising under it would set it up at once); where the reef ahead stands higher than that
          // lets it rise, held where it was until it may (never into the floor, never set onto it).
          let y = next.y + (steer.y - next.y) * b;
          if (fish.at) {
            const most = STEER_RISE_BL_PER_S * length * run;
            y = Math.min(Math.max(y, fish.at.y - most), fish.at.y + most);
            if (this.floorAt(next.x, next.z) > y) {
              [next.x, next.z] = [fish.at.x, fish.at.z];
              y = Math.min(fish.at.y + most, Math.max(y, this.floorAt(next.x, next.z)));
            }
          }
          next.y = Math.min(Math.max(y, this.floorAt(next.x, next.z)), top);
          // Its place in its school where it is drawn, so as the interaction lets it go it swims back from there at its
          // school's own drawing in (`MOST_BL_PER_S`), never set back at once.
          fish.off = [next.x - this.middle.x, next.z - this.middle.z];
        } else if (this.settling.has(fish.id) && fish.at) {
          // Let go: back to its school's height no faster than it was steered (M265's: set back at once, a nenue let go
          // at the limu rose 5.8 of its lengths in a frame's second).
          const most = STEER_RISE_BL_PER_S * length * run;
          const y = Math.min(Math.max(next.y, fish.at.y - most), fish.at.y + most);
          if (Math.abs(y - next.y) < 1e-4) this.settling.delete(fish.id);
          next.y = Math.max(y, this.floorAt(next.x, next.z));
        }
        // The way it last moved on the level: rising to cruise or dropping to feed turns no fish about.
        if (fish.at && Math.hypot(next.x - fish.at.x, next.z - fish.at.z) > 1e-9) fish.moved.set(next.x - fish.at.x, 0, next.z - fish.at.z);
        // The way it swims: its school's middle's and its own about it where given, else the way it moved (a patrolling
        // male's), each only while the game runs.
        if (run > 0) {
          // Steered at all, the way it is steered or moves (M265): swimming back to its school as the blend falls, it faces
          // the way it goes, not its school's.
          if (steer && b > 0 && steer.face) fish.way.set(steer.face[0], 0, steer.face[1]);
          else if (steer && b > 0 && fish.at) fish.way.set(next.x - fish.at.x, 0, next.z - fish.at.z).multiplyScalar(1 / run);
          else if (swimX !== undefined && swimZ !== undefined) fish.way.set(middleWay.x + swimX, 0, middleWay.z + swimZ);
          else if (fish.at) fish.way.set(next.x - fish.at.x, 0, next.z - fish.at.z).multiplyScalar(1 / run);
        }
        fish.at = next;
        return k;
      };
      const first = d;
      for (const f of s.fish) {
        // One whose place lies over or beyond land the search finds turns back toward its school's point, at its
        // commute's pace.
        const swim = calm ? 0 : pace * bl;
        const k = lay(f.x * bl, f.z * bl, bl, lift, (f.hx - travel[0]) * swim, (f.hz - travel[1]) * swim);
        if (this.individual && run > 0 && !calm) this.around(f, bl, run);
        const r = Math.hypot(f.x, f.z);
        if (k < 1 && r > 0) {
          const back = 1 - Math.min(r * (1 - k), COMMUTE_BL_PER_S * run) / r;
          f.x *= back;
          f.z *= back;
        }
      }
      const male = this.lengthOf(s.kind, 1);
      for (let k = 0; k < s.males; k++) {
        // His patrol slowed through the night as his school's milling is.
        const [x, z] = patrol(k, s.males, s.seconds, s.phase);
        lay(x * bl, z * bl, male, REST_BL + (cruiseBL - REST_BL) * awake);
      }
      // A dive's foot passed, down on the reef by day and out: a bite there, under one of its fish (M173), its own by the
      // dive's number, not the school's stream, so its swimming is the same with or without one listening.
      const dive = divesDown(s.seconds, s.phase);
      if (this.onBite && swimOf.grazes !== false && !ownFeeds && dive > divesDown(before, s.phase) && daylight * awake > 0.5 && s.fish.length > 0) {
        const fish = this.drawnFish[first + Math.floor(hash3(s.seed, dive, 1_740) * s.fish.length)];
        if (fish?.at && fish.floor !== null) this.onBite(s.kind, fish.at.x, fish.floor, fish.at.z, (s.seed * 7_919 + dive) | 0);
      }
    });
  }

  /**
   * Each fish drawn where it is, sized in the scene, facing the way it swims across the screen from `camera` (turned
   * about once its way is `FACE_SIN` past square the other way, at most once in `TURN_HOLD_S`, M159),
   * dimmed at home; at least `minPx` pixels long on a canvas `canvasPx` high (M97, the brief's section 5: "a minimum
   * size on screen where needed", a rendering choice: a fish far off drawn larger than its length, never smaller
   * than can be seen); the level of detail eased toward the view's over `seconds` since the last (`FISH_COVER`), at once
   * when not given, measured over the part of the canvas the panels leave (`visible`, its left, right, foot and top in
   * normalized device coordinates; the whole canvas when not given).
   */
  fit(camera: THREE.Camera, canvasPx = 0, minPx = 0, seconds = Infinity, visible: Visible = WHOLE): void {
    const here = new THREE.Vector3();
    const before = new THREE.Vector3();
    const view = new THREE.Vector3();
    // Pixels a scene unit at a unit's depth: the projection's vertical scale over the canvas's half height.
    const pxAtUnit = ((camera as THREE.PerspectiveCamera).projectionMatrix?.elements[5] ?? 0) * (canvasPx / 2);
    // Each fish's size on screen, how much of the screen those in view cover, and how many are big enough to tell (M105);
    // the canvas's shape its own, not the larger frame a view offset centers the camera in (`scene.ts`).
    const lens = camera as THREE.PerspectiveCamera;
    const aspect = lens.view?.enabled ? lens.view.width / lens.view.height : lens.aspect ?? 1;
    let cover = 0;
    let told = 0;
    const [eye, look] = [new THREE.Vector3(), new THREE.Vector3()];
    // Each fish coming in or leaving faded toward its drawing's whole or none (M266, `RECONCILE.fadeS`); those faded out
    // gone from their schools.
    let gone = false;
    if (Number.isFinite(seconds)) this.timed = true;
    for (const f of this.drawnFish) {
      const target = this.leaving(f) ? 0 : 1;
      f.fade = fadeToward(f.fade, target, seconds, RECONCILE.fadeS);
      // Pruned only where it can be (review 31: a male faded below one still fading kept the drawing rebuilt each frame).
      if (target === 0 && f.fade <= 0 && (f.member || f.post === (this.schools[f.school] as School).males - 1)) gone = true;
    }
    if (gone) this.prune();
    const sized = this.drawnFish.map((f) => {
      if (!f.at) return null;
      const s = this.schools[f.school] as School;
      const kind = this.kinds[s.kind] as FishKind;
      const sprite = (f.phase === 0 ? kind.initial : kind.terminal) as Sprite;
      let length = this.lengthOf(s.kind, f.phase);
      const depth = -view.copy(f.at).applyMatrix4(camera.matrixWorldInverse).z;
      if (minPx > 0 && pxAtUnit > 0 && depth > 0) length = Math.max(length, (minPx * depth) / pxAtUnit);
      const height = (length * sprite.height) / sprite.width;
      here.copy(f.at).project(camera);
      // Those in view, as much of each as is drawn: one passing nearer the camera is faded (`NEAR_FADE`).
      f.seen = depth > 0 && here.x >= visible[0] && here.x <= visible[1] && here.y >= visible[2] && here.y <= visible[3];
      if (pxAtUnit > 0 && f.seen) {
        const [w, h] = [(length * pxAtUnit) / depth, (height * pxAtUnit) / depth];
        const near = nearFade(w, canvasPx * aspect);
        cover += w * h * near;
        if (w >= FISH_COVER.toldPx) told += near;
      }
      // The way it swims across the screen (`Drawn.way`, seen from the camera, `facesRight`'s reckoning): turned about once
      // it swims more than `FACE_SIN` past square to the camera the other way (M159); first drawn, the way it swims, or
      // else its last step.
      // One an interaction steers wholly, the way it is seen to move across the screen this frame, where it moves (review
      // M267's: its world way, near square to the camera on a dart or a hop to its food, left it drawn backward a second or
      // more); held as long as any fish once turned (a quarter of the hold flickered, 35 to 46 turns back within a second).
      // (Its nose to its food where the interaction gives it one, `FishSteer.face`, as before: a bite's stroke back turns it not.)
      const steering = this.steers.get(f.id);
      const steered = !!steering && !steering.face && steering.blend >= 0.9 && f.moved.lengthSq() > 1e-12;
      const seenDx = steered ? here.x - before.copy(f.at).sub(f.moved).project(camera).x : 0;
      const across = steered && seenDx !== 0 ? Math.sign(seenDx) : acrossOf(camera, f.at, f.way, eye, look);
      if (f.right === null) f.right = across !== 0 ? across > 0 : here.x > before.copy(f.at).sub(f.moved).project(camera).x;
      else if (f.held >= TURN_HOLD_S && (f.right ? across < -FACE_SIN : across > FACE_SIN)) [f.right, f.held] = [!f.right, 0];
      f.held += Number.isFinite(seconds) ? seconds : TURN_HOLD_S;
      return { f, s, sprite, length, height, right: f.right };
    });
    // Fewer fish drawn as they would cover more of the screen than `FISH_COVER.most`, or more of them be big enough to
    // tell than `FISH_COVER.told`, eased; all where the canvas is unknown.
    const screen = canvasPx * canvasPx * aspect * ((visible[1] - visible[0]) / 2) * ((visible[3] - visible[2]) / 2);
    const want = screen > 0 && cover > 0 ? Math.min(1, (FISH_COVER.most * screen) / cover, told > 0 ? FISH_COVER.told / told : 1) : 1;
    this.keep = screen > 0 ? this.keep + (want - this.keep) * (1 - Math.exp(-seconds / FISH_COVER.easeS)) : 1;
    // Taken to the whole level once within a thousandth of it (M266's review: eased, it stalled a hair under 1, so the
    // fish keyed 0.9 and over, thinned once, never came back).
    if (want >= 1 && this.keep > 0.999) this.keep = 1;
    // Each fish drawn or not at the level by its own key, kept so until the level moves past it by a margin (M266: a
    // fish whose key sat at the level faded in and out as fish swam in and out of view), faded over time, not by its
    // key's band (a quick zoom popped them); the one followed, its school and a hunt's fish always drawn.
    for (const f of this.drawnFish) {
      const pinned = (this.schools[f.school] as School).seed === this.held || f.id === this.heldFish || this.poses.has(f.id);
      f.lodOn = pinned || heldInLevel(f.lodOn, f.key, this.keep);
      f.lod = fadeToward(f.lod, f.lodOn ? 1 : 0, seconds, LOD_HOLD.fadeS);
    }
    const draws: FishDraw[] = [];
    let cast = 0;
    const posable: PosableFish[] = [];
    for (const d of sized) {
      if (!d) continue;
      // An interaction's fish held drawn as it was when the interaction began, while it lasts (M265, `FishSteer.hold`).
      const steer = this.steers.get(d.f.id);
      // Not one leaving the drawing (review 31: held whole by an interaction, it faded nowhere and was gone in a frame).
      const shown = steer && !this.leaving(d.f) ? Math.max(this.shownOf(d.f), Math.min(1, steer.hold ?? 0)) : this.shownOf(d.f);
      if (shown <= 0) continue;
      const tilt = steer && steer.tilt ? steer.tilt * Math.min(1, Math.max(0, steer.blend)) : 0;
      const alpha = shown * ((this.kinds[d.s.kind] as FishKind).swim.restsInOpen ? 1 : SHELTERED_OPACITY + (1 - SHELTERED_OPACITY) * d.s.awake);
      const own = d.f.at as THREE.Vector3;
      posable.push({ id: d.f.id, species: (this.kinds[d.s.kind] as FishKind).species, kind: d.s.kind, phase: d.f.phase, at: own, width: d.length, height: d.height, right: d.right, shown: alpha, drawn: shown, sprite: d.sprite, school: d.s.seed, tlMm: this.mmOf(d.s.kind, d.f.phase), feeds: d.s.daylight * d.s.awake, awake: d.s.awake });
      // A hunt's pose where it has one (M251): drawn its blend of the way to the hunt's place, as the hunt draws it.
      const pose = this.poses.get(d.f.id);
      if (pose) {
        const b = Math.min(1, Math.max(0, pose.blend));
        const size = pose.scale ?? 1;
        // Its facing the hunt's while it holds it, and free to turn to its own way at once as it is given back.
        if (pose.right !== undefined && b > 0.5) [d.f.right, d.f.held] = [pose.right, TURN_HOLD_S];
        draws.push({
          x: own.x + (pose.at.x - own.x) * b, y: own.y + (pose.at.y - own.y) * b, z: own.z + (pose.at.z - own.z) * b,
          width: d.length * size * (pose.scaleX ?? 1),
          height: d.height * size,
          rect: this.rects.get(d.sprite) as [number, number, number, number],
          right: pose.right !== undefined && b > 0.5 ? pose.right : d.right,
          alpha: alpha * (pose.alpha ?? 1),
          ...(pose.flash ? { flash: pose.flash } : {}),
          ...(tilt && b <= 0.5 ? { tilt } : {}),
        });
      } else {
        draws.push({
          x: own.x, y: own.y, z: own.z,
          width: d.length,
          height: d.height,
          rect: this.rects.get(d.sprite) as [number, number, number, number],
          // The art faces left: turned over when it faces right across the screen.
          right: d.right,
          alpha,
          ...(tilt ? { tilt } : {}),
        });
      }
      // Its shadow's caster (M246): its body's middle, its drawn length, a fish's breadth from above, the way it swims,
      // as drawn: where a hunt's pose has drawn it, as long and as faded (M256, review M253's: it had cast from its own
      // swimming). Its object kept from frame to frame (review M246's).
      const drawn = draws[draws.length - 1] as FishDraw;
      const c = this.castersNow[cast] ?? (this.castersNow[cast] = { x: 0, y: 0, z: 0, length: 0, width: FISH_BREADTH, headX: 1, headZ: 0, alpha: 0 });
      [c.x, c.y, c.z, c.length, c.headX, c.headZ, c.alpha] = [drawn.x, drawn.y + drawn.height / 2, drawn.z, drawn.width, d.f.way.x, d.f.way.z, shown * (pose?.alpha ?? 1)];
      cast++;
    }
    this.castersNow.length = cast;
    this.lastDrawn = posable;
    this.batch.set(draws);
  }

  /** Whether a fish is leaving the drawing (M266): a member marked so, or a male past his school's posts kept. */
  private leaving(f: Drawn): boolean {
    const s = this.schools[f.school] as School;
    return f.member ? f.member.leaving : f.post >= s.males - s.malesLeaving;
  }

  /** The fish faded out of the drawing taken from their schools, and the schools left with none (M266). */
  private prune(): void {
    const faded = new Set(this.drawnFish.filter((f) => f.fade <= 0 && this.leaving(f)).map((f) => f.id));
    for (const s of this.schools) {
      s.fish = s.fish.filter((m) => !(m.leaving && faded.has(memberId(m))));
      // Its males from the last post down, each once faded (review 31: all at once only when every one had, so males that
      // began leaving on different days held the drawing rebuilt each frame).
      while (s.malesLeaving > 0 && faded.has(`${s.seed}.1.${s.males - 1}`)) [s.males, s.malesLeaving] = [s.males - 1, s.malesLeaving - 1];
    }
    // A pose of one gone let go (review 31: a male's name, his school's and his post's, comes back with the post, and
    // took up the eaten pose of the one before him, hidden for good).
    for (const id of faded) this.poses.delete(id);
    const before = new Map(this.drawnFish.map((d) => [d.id, d]));
    this.schools = this.schools.filter((s) => s.fish.length + s.males > 0);
    this.drawnFish = this.drawnOf(before);
  }

  /** The fish drawn now as their shadows' casters (M246), as `fit` last laid them. */
  casters(): readonly Caster[] {
    return this.castersNow;
  }

  /** Whether a fish of that name is in the drawing, shown or thinned (M266). */
  has(id: string): boolean {
    return this.drawnFish.some((d) => d.id === id);
  }

  /** Each fish as last drawn (M251): a hunt's to choose among. */
  posable(): readonly PosableFish[] {
    return this.lastDrawn;
  }

  /** A fish drawn by a hunt's pose from now (M251), or given back to its own swimming (null). */
  pose(id: string, pose: FishPose | null): void {
    if (pose) this.poses.set(id, pose);
    else this.poses.delete(id);
  }

  /** How much of a fish is drawn now, 0 to 1: as it comes in or leaves (`fade`, M266) and at the level of detail (`lod`, held and faded in time, M266; all of a followed school's and the fish followed). */
  private shownOf(f: Drawn): number {
    return f.fade * f.lod;
  }

  /** Whether a fish is drawn at the level of detail now, half seen or more (M105's review: one fading in could not be tapped yet was seen). */
  private kept(f: Drawn): boolean {
    return this.shownOf(f) >= 0.5;
  }

  /** A fish steered by the reef's interactions from now (M265, `FishSteer`), or given back to its school (null). */
  steer(id: string, steer: FishSteer | null): void {
    if (steer) {
      this.steers.set(id, steer);
      this.settling.delete(id);
    } else if (this.steers.delete(id)) this.settling.add(id);
  }

  /** The kinds whose feeding the reef's interactions draw (M265): their schools' dives to the reef, and the bites at them, left off. */
  setOwnFeeding(kinds: ReadonlySet<number>): void {
    this.ownFeeding = kinds;
  }

  /** A school, the nearest to `point` of two fish or more, split now and rejoining (`SCHOOL_LIFE`, the developer's, M265); its seed, or null for none. */
  splitNear(point: THREE.Vector3): number | null {
    let best: School | null = null;
    let near = Infinity;
    for (const s of this.schools) {
      if (s.fish.length < 2 || !s.lastMiddle) continue;
      const d = s.lastMiddle.distanceTo(point);
      if (d < near) [near, best] = [d, s];
    }
    if (!best) return null;
    this.splitAt.set(best.seed, best.seconds);
    return best.seed;
  }

  /** Each fish its own ways among its school from now (`SCHOOL_LIFE`, M265): the Reef's. */
  setIndividual(on: boolean): void {
    this.individual = on;
  }

  /**
   * A school's fish's own ways at its swimming's seconds (`SCHOOL_LIFE`): each one's pace and room, the schoolmate it
   * follows in each of its windows, and its third's goal off the school's middle in a split; each by its own draws, so
   * the same school does the same at the same seconds.
   */
  private ownWays(s: School, bl: number): void {
    const L = SCHOOL_LIFE;
    const t = s.seconds;
    const n = s.fish.length;
    const leadWindow = Math.floor(t / L.leadS);
    const splitWindow = Math.floor(t / L.splitS);
    const into = t - splitWindow * L.splitS;
    const forced = this.splitAt.get(s.seed);
    const sinceForced = forced === undefined ? Infinity : t - forced;
    if (sinceForced >= L.splitForS) this.splitAt.delete(s.seed);
    const splitting = sinceForced < L.splitForS || (n >= L.splitLeast && hash3(s.seed, splitWindow, 2_651) < L.splitOdds && into < L.splitForS);
    const third = Math.floor(hash3(s.seed, splitWindow, 2_652) * Math.min(3, n));
    let angle = 2 * Math.PI * hash3(s.seed, splitWindow, 2_653);
    // Its group's goal inside the box its fish are kept in, `insetM` in (M265's: a group drawn out past it pressed its fish
    // on the box's sides): turned the other way where it would not be, and none where neither would.
    const inside = (a: number): boolean => {
      if (!this.keepIn || !this.roam) return true;
      const [x, z] = [this.middle.x + L.splitBL * bl * Math.cos(a), this.middle.z + L.splitBL * bl * Math.sin(a)];
      const [{ min, max }, m] = [this.keepIn, this.roam.insetM];
      return x >= (min[0] as number) + m && x <= (max[0] as number) - m && z >= (min[2] as number) + m && z <= (max[2] as number) - m;
    };
    if (!inside(angle)) angle += Math.PI;
    const kept = inside(angle);
    // Out and back over its time apart, smoothly.
    const apart = splitting && kept ? L.splitBL * Math.sin((Math.PI * (sinceForced < L.splitForS ? sinceForced : into)) / L.splitForS) ** 2 : 0;
    s.fish.forEach((f, i) => {
      f.pace = 1 + L.paceSwing * Math.sin(2 * Math.PI * (t / L.paceS + hash3(s.seed, i, 2_654)));
      f.space = L.space[0] + (L.space[1] - L.space[0]) * hash3(s.seed, i, 2_655);
      if (hash3(s.seed, i * 977 + leadWindow, 2_656) < L.leadOdds && n > 1) {
        // The nearest of its schoolmates, as its window begins (kept through it while it is the same window).
        if (f.lead === undefined || f.leadWindow !== leadWindow) {
          let best = -1;
          let near = Infinity;
          s.fish.forEach((g, j) => {
            const d = j === i ? Infinity : Math.hypot(g.x - f.x, g.z - f.z);
            if (d < near) [near, best] = [d, j];
          });
          f.lead = best >= 0 ? best : undefined;
          f.leadWindow = leadWindow;
        }
      } else f.lead = undefined;
      f.goal = apart > 0 && i % 3 === third ? [apart * Math.cos(angle), apart * Math.sin(angle)] : undefined;
    });
  }

  /**
   * A fish turned aside from rock rising `SCHOOL_LIFE.obstacleBL` of its lengths over its school's point (M265): its place
   * in its school (body lengths) moved down the rock's slope at `aroundBLPerS`, so its school parts round an outcrop.
   */
  private around(f: Swimmer, bl: number, run: number): void {
    if (Math.hypot(f.x, f.z) >= SCHOOL_LIFE.aroundMostBL) return;
    const [x, z] = [this.middle.x + f.x * bl, this.middle.z + f.z * bl];
    if (this.floorAt(x, z) - this.middle.y < SCHOOL_LIFE.obstacleBL * bl) return;
    const e = Math.max(0.25, bl);
    const gx = this.floorAt(x + e, z) - this.floorAt(x - e, z);
    const gz = this.floorAt(x, z + e) - this.floorAt(x, z - e);
    const g = Math.hypot(gx, gz);
    if (g < 1e-9) return;
    const step = SCHOOL_LIFE.aroundBLPerS * run;
    // Not past the box its fish are kept in (as `ownWays`' goal).
    if (this.keepIn) {
      const [nx, nz] = [x - (gx / g) * step * bl, z - (gz / g) * step * bl];
      const { min, max } = this.keepIn;
      if (nx < (min[0] as number) + 0.5 || nx > (max[0] as number) - 0.5 || nz < (min[2] as number) + 0.5 || nz > (max[2] as number) - 0.5) return;
    }
    f.x -= (gx / g) * step;
    f.z -= (gz / g) * step;
  }

  /** The school followed, by its seed, all its sprites drawn whatever the level of detail; null for none. */
  hold(seed: number | null): void {
    this.held = seed;
  }

  /** The fish followed, by its name (M266), drawn whatever the level of detail and the last to leave; null for none. */
  holdFish(id: string | null): void {
    this.heldFish = id;
  }

  /** The share of the fish drawn at the camera's level of detail now (`FISH_COVER`). */
  level(): number {
    return this.keep;
  }

  /** The water the fish are seen through (`FishBatch.fogUniforms`). */
  fogUniforms(): ReturnType<FishBatch['fogUniforms']> {
    return this.batch.fogUniforms();
  }

  /**
   * The school of the fish drawn nearest a point on the screen (`x`, `y` in normalized device coordinates, -1 to 1),
   * within `withinPx` pixels of it on a canvas `widthPx` by `heightPx`, by its seed, which a day's new counts keep;
   * -1 where none is (M97, Follow Wildlife).
   */
  pick(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): number {
    return this.pickNear(camera, x, y, widthPx, heightPx, withinPx)?.seed ?? -1;
  }

  /** As `pick`, with how far the tap is from the fish that took it on the screen, px, and from the camera, scene units; null where none is (M158: to weigh it against an octopus's; M210's second review: against a whale's). */
  pickNear(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): { seed: number; id: string; px: number; away: number } | null {
    const at = new THREE.Vector3();
    let best = withinPx;
    let school = -1;
    let away = 0;
    let id = '';
    for (const f of this.drawnFish) {
      // None hidden by a hunt or leaving the drawing (M266's review: followed, it was lost the next frame).
      if (!f.at || !this.kept(f) || this.leaving(f) || this.poses.get(f.id)?.alpha === 0) continue;
      at.copy(f.at).project(camera);
      if (at.z < -1 || at.z > 1) continue;
      const d = Math.hypot(((at.x - x) * widthPx) / 2, ((at.y - y) * heightPx) / 2);
      if (d < best) {
        best = d;
        school = (this.schools[f.school] as School).seed;
        away = f.at.distanceTo(camera.position);
        id = f.id;
      }
    }
    return school < 0 ? null : { seed: school, id, px: best, away };
  }

  /**
   * A fish drawn, by its name (M266, Follow Animal): where it is drawn, the seed of the school it swims in now (a day's new
   * counts may move it to another of its kind's), whether it is a terminal male, its phase's mean length as the day's
   * data gives it, mm, and whether a hunt has taken it (`pose` at alpha 0); null where it is not drawn or is leaving.
   */
  fishAt(id: string): { seed: number; at: THREE.Vector3; terminal: boolean; lengthMm: number; eaten: boolean } | null {
    const f = this.drawnFish.find((d) => d.id === id);
    const s = f ? this.schools[f.school] : undefined;
    if (!f?.at || !s || this.leaving(f)) return null;
    // Where it is drawn: its hunt's blend of the way to the hunt's place where it is posed (review 31: the camera kept on
    // its school's place for it while the hunt drew it at the predator's mouth or darting off).
    const pose = this.poses.get(id);
    const b = pose ? Math.min(1, Math.max(0, pose.blend)) : 0;
    const at = pose && b > 0 ? f.at.clone().lerp(pose.at, b) : f.at;
    return { seed: s.seed, at, terminal: f.phase === 1, lengthMm: this.mmOf(s.kind, f.phase), eaten: pose?.alpha === 0 };
  }

  /**
   * A school as the wildlife card tells it, by its seed (M97): its kind, its island, its sprites and the fish they
   * stand for, where its fish are on average, and how far out and awake it is; null for none (gone with its fish).
   */
  schoolAt(seed: number): { kind: number; island: number; sprites: number; fish: number; perSprite: number; middle: THREE.Vector3; share: number; awake: number; travelling: number } | null {
    const index = this.schools.findIndex((x) => x.seed === seed);
    const s = this.schools[index];
    if (!s) return null;
    // Its fish staying (review 31: one no longer wanted, all its fish leaving, was followed and told as "0 fish").
    const mine = this.drawnFish.filter((f) => f.school === index && !this.leaving(f));
    const placed = mine.filter((f) => f.at);
    if (placed.length === 0) return null;
    const middle = placed.reduce((m, f) => m.add(f.at as THREE.Vector3), new THREE.Vector3()).multiplyScalar(1 / placed.length);
    // Its fish the same whatever is drawn; its sprites those the level of detail draws, each standing for its share, both
    // of the fish staying (review 31: a fish fading out counted a sprite and not a fish, "standing for 0 fish").
    const fish = mine.length * this.perSpriteOf(s.kind);
    const sprites = Math.max(1, mine.filter((f) => f.lodOn).length);
    return { kind: s.kind, island: s.island, sprites, fish, perSprite: fish / sprites, middle, share: s.share, awake: s.awake, travelling: s.travelling };
  }

  /** The seeds of a kind's schools on an island, lowest first (M156: the school a tag stands over where none is remembered). */
  seedsOf(kind: number, island: number): number[] {
    return this.schools.filter((s) => s.kind === kind && s.island === island && FishSchools.staying(s)).map((s) => s.seed).sort((a, b) => a - b);
  }

  /** Whether a school has fish staying, not only ones leaving (review 31: a school no longer wanted was named and followed). */
  private static staying(s: School): boolean {
    return s.fish.some((f) => !f.leaving) || s.males > s.malesLeaving;
  }

  /** The school of a kind whose fish are drawn nearest a point, by its seed; null where none of that kind is drawn (M147: a visitor followed home). */
  nearestOf(kind: number, point: THREE.Vector3): number | null {
    let best: number | null = null;
    let nearest = Infinity;
    for (const s of this.schools) {
      if (s.kind !== kind) continue;
      const at = this.schoolAt(s.seed);
      const d = at ? at.middle.distanceTo(point) : Infinity;
      if (d < nearest) [best, nearest] = [s.seed, d];
    }
    return best;
  }

  /** How many sprites of each kind are drawn, and the fish they stand for (`perSprite` each). */
  spritesOf(kind: number): number {
    return this.schools.reduce((n, s) => (s.kind === kind ? n + s.fish.filter((f) => !f.leaving).length + s.males - s.malesLeaving : n), 0);
  }

  /** The light the fish are seen by (`FishBatch.setLight`). */
  setLight(r: number, g: number, b: number): void {
    this.batch.setLight(r, g, b);
  }

  /** The light the fish are seen by, for the tests. */
  light(): [number, number, number] {
    return this.batch.light();
  }

  dispose(): void {
    this.batch.dispose();
  }
}
